import axios from "axios";
import useReaderStore from "../context/readerStore";
import {
  DEFAULT_AI_LANGUAGE,
  DEFAULT_AI_LANGUAGE_NAME,
  toAiLanguageName,
} from "../i18n/aiLanguages";
import { getSessionTokens, getValidRstToken } from "./igpService";

const AI_TIMEOUT_MS = 120000;

let IGP_AI_BASE_URL = (
  process.env.REACT_APP_IGP_AI_BASE_URL || "https://api.ai.igpublish.com"
).replace(/\/$/, "");

export class AiError extends Error {
  constructor({ status, code, message, body } = {}) {
    super(message || "AI request failed");
    this.name = "AiError";
    this.status = status;
    this.code = code;
    this.body = body;
  }
}

let conversationId = null;
let conversationDocumentId = null;

// Cache only when ai_available is true so processing/failed can be rechecked.
const availabilityCache = new Map();
const availabilityInflight = new Map();

// Session-scoped AI preferences (grant session — not document-specific).
let preferencesCache = null;
let preferencesInflight = null;
// Serialize preference PUTs so rapid language/toggle updates cannot race.
let preferencesWriteChain = Promise.resolve();

// Session cache: documentId::type::scopeKey::language -> answer text
const summaryCache = new Map();

export const peekAiAvailability = (documentId) => {
  if (!documentId) return null;
  return availabilityCache.get(documentId) || null;
};

export const clearAiAvailabilityCache = (documentId) => {
  if (documentId) {
    availabilityCache.delete(documentId);
    availabilityInflight.delete(documentId);
    return;
  }
  availabilityCache.clear();
  availabilityInflight.clear();
};

export const clearAiPreferencesCache = () => {
  preferencesCache = null;
  preferencesInflight = null;
};

/**
 * Clear all in-memory AI session state (prefs, summaries, availability, chat, conversation).
 * Call on logout / session expiry / failed session restore.
 */
export const clearAiSessionCaches = () => {
  clearAiPreferencesCache();
  clearAiAvailabilityCache();
  clearAiSummaryCache();
  clearAskAiMessages();
  resetAiConversation();
};

const summaryCacheKey = (
  documentId,
  type,
  scopeKey = "",
  language = getAiTargetLanguage(),
) =>
  `${documentId}::${type}::${scopeKey}::${language || DEFAULT_AI_LANGUAGE_NAME}`;

const summaryInflight = new Map();

export const peekAiSummary = (documentId, type, scopeKey = "") => {
  if (!documentId || !type) return null;
  return summaryCache.get(summaryCacheKey(documentId, type, scopeKey)) || null;
};

export const rememberAiSummary = (documentId, type, answer, scopeKey = "") => {
  if (!documentId || !type || !answer) return;
  summaryCache.set(summaryCacheKey(documentId, type, scopeKey), answer);
};

export const clearAiSummaryCache = (documentId) => {
  if (!documentId) {
    summaryCache.clear();
    summaryInflight.clear();
    return;
  }
  const prefix = `${documentId}::`;
  Array.from(summaryCache.keys()).forEach((key) => {
    if (key.startsWith(prefix)) summaryCache.delete(key);
  });
  Array.from(summaryInflight.keys()).forEach((key) => {
    if (key.startsWith(prefix)) summaryInflight.delete(key);
  });
};

/**
 * Most recently cached summary selection for a document (Map insertion order).
 * Only considers entries for the current AI language.
 * Used to restore Summary tab UI after sidebar remount — welcome only when empty.
 */
export const peekLatestCachedSummarySelection = (documentId) => {
  if (!documentId) return null;
  const prefix = `${documentId}::`;
  const langSuffix = `::${getAiTargetLanguage() || DEFAULT_AI_LANGUAGE_NAME}`;
  let latest = null;
  for (const [key, answer] of summaryCache.entries()) {
    if (!key.startsWith(prefix) || !answer) continue;
    if (!key.endsWith(langSuffix)) continue;
    const rest = key.slice(prefix.length, key.length - langSuffix.length);
    const sep = rest.indexOf("::");
    if (sep <= 0) continue;
    latest = {
      type: rest.slice(0, sep),
      scopeKey: rest.slice(sep + 2),
      answer,
    };
  }
  return latest;
};

/** Drop one summary cache/inflight slot so the next fetch regenerates. */
export const forgetAiSummary = (documentId, type, scopeKey = "") => {
  if (!documentId || !type) return;
  const key = summaryCacheKey(documentId, type, scopeKey);
  summaryCache.delete(key);
  summaryInflight.delete(key);
};

/**
 * Returns cached summary, joins an in-flight request, or starts a new one.
 * Survives sidebar close — result is always written to session cache.
 * Pass `onUpdate` / `signal` to stream tokens (ai_v5 `is_streaming`).
 */
export const fetchAiSummary = async ({
  documentId,
  type,
  scopeKey = "",
  summaryMode = "quick",
  summarizeScope = "book",
  chapter,
  pageNumber,
  onUpdate,
  signal,
} = {}) => {
  if (!documentId || !type) {
    throw new AiError({ message: "Missing document or summary type" });
  }

  const key = summaryCacheKey(documentId, type, scopeKey);
  const cached = summaryCache.get(key);
  if (cached) return cached;

  // Streaming callers need their own onUpdate — do not join a shared inflight.
  const canShareInflight = typeof onUpdate !== "function" && !signal;
  if (canShareInflight) {
    const inflight = summaryInflight.get(key);
    if (inflight) return inflight;
  }

  const contextOverrides = {};
  if (chapter) contextOverrides.chapter = chapter;
  if (pageNumber != null) contextOverrides.page_number = pageNumber;

  const actionParams = {
    summary_mode: summaryMode,
  };
  // by_chapter implies chapter scope per docs; still send scope when known.
  if (summarizeScope) {
    actionParams.summarize_scope = summarizeScope;
  }

  const request = executeAiActionStream({
    action_type: "summarize",
    prompt: "",
    content: "",
    action_params: actionParams,
    context_overrides: contextOverrides,
    onUpdate,
    signal,
  })
    .then((data) => {
      const answer = data?.answer || "";
      if (answer) {
        summaryCache.set(key, answer);
      }
      if (canShareInflight) summaryInflight.delete(key);
      return answer;
    })
    .catch((error) => {
      if (canShareInflight) summaryInflight.delete(key);
      throw error;
    });

  if (canShareInflight) summaryInflight.set(key, request);
  return request;
};

export const rememberConversationId = (id) => {
  if (id) conversationId = id;
};

export const resetAiConversation = () => {
  conversationId = null;
  conversationDocumentId = null;
};

// Ask AI chat bubbles — session memory (survives sidebar remount, cleared on reload).
const askAiMessagesByDoc = new Map();

export const peekAskAiMessages = (documentId) => {
  if (!documentId) return [];
  return askAiMessagesByDoc.get(documentId) || [];
};

export const rememberAskAiMessages = (documentId, messages) => {
  if (!documentId) return;
  askAiMessagesByDoc.set(documentId, messages || []);
};

export const clearAskAiMessages = (documentId) => {
  if (!documentId) {
    askAiMessagesByDoc.clear();
    return;
  }
  askAiMessagesByDoc.delete(documentId);
};

const getAiBaseUrl = () => IGP_AI_BASE_URL;

const getAuth = async () => {
  const {
    grantId,
    agtToken,
    rstToken: sessionRst,
    documentId,
  } = getSessionTokens();
  const rstToken = String(
    sessionRst || (await getValidRstToken(grantId, agtToken)) || "",
  ).trim();
  if (!rstToken) {
    throw new AiError({
      message: "No RST token for AI request",
      code: "MISSING_RST",
    });
  }
  return { grantId, rstToken, documentId };
};

const authHeaders = (rstToken) => ({
  Authorization: `Bearer ${rstToken}`,
  Accept: "application/json",
});

const jsonAuthHeaders = (rstToken) => ({
  ...authHeaders(rstToken),
  "Content-Type": "application/json",
});

const unwrapData = (response) => response?.data?.data ?? response?.data;

const toAiError = (error) => {
  if (error instanceof AiError) return error;
  const resp = error?.response;
  const body = resp?.data;
  const err = body?.error || body;
  return new AiError({
    status: resp?.status,
    code: err?.code,
    message: err?.message || error.message,
    body,
  });
};

export const deriveChapterLabel = (
  metadata,
  currentPage,
  epubCurrentChapterNumber,
) => {
  const chapters = metadata?.chapters;
  if (Array.isArray(chapters) && chapters.length) {
    const match = chapters.find((chapter) => {
      const start = Number(chapter.start_page) || 0;
      const end = Number(chapter.end_page) || 0;
      return currentPage >= start && currentPage <= end;
    });
    if (match) {
      const num = Number(match.chapter_number) || epubCurrentChapterNumber || 1;
      const title = match.title || match.chapter_title;
      return title || `Chapter ${num}`;
    }
  }
  const num = Number(epubCurrentChapterNumber) || 1;
  return `Chapter ${num}`;
};

/**
 * Build the chapter list for chapter-by-chapter summary (TOC / metadata).
 * Prefers metadata.chapters; then metadata.table_of_contents; then reader toc.
 */
export const listAiChapters = (metadata, toc = []) => {
  if (Array.isArray(metadata?.chapters) && metadata.chapters.length) {
    return metadata.chapters.map((chapter, index) => {
      const num = Number(chapter.chapter_number) || index + 1;
      const title = String(chapter.title || chapter.chapter_title || "").trim();
      const label = title || `Chapter ${num}`;
      return {
        id: `ch-${num}-${chapter.start_page ?? index}`,
        label,
        contextChapter: label,
        startPage: Number(chapter.start_page) || null,
      };
    });
  }

  if (
    Array.isArray(metadata?.table_of_contents) &&
    metadata.table_of_contents.length
  ) {
    return metadata.table_of_contents
      .map((entry, index) => {
        const title = String(entry?.title || "").trim();
        if (!title) return null;
        const startPage = Number(entry?.start_page) || null;
        return {
          id: `meta-toc-${index}-${startPage ?? "x"}`,
          label: title,
          contextChapter: title,
          startPage,
        };
      })
      .filter(Boolean);
  }

  if (Array.isArray(toc) && toc.length) {
    return toc
      .map((item, index) => {
        const title = String(
          item.title || item.label || item.chapterTitle || "",
        ).trim();
        if (!title) return null;
        const num = Number(item.chapterNumber) || index + 1;
        return {
          id: `toc-${num}-${item.pageNumber ?? item.startPage ?? index}`,
          label: title,
          contextChapter: title,
          startPage:
            Number(item.pageNumber || item.startPage || item.page) || null,
        };
      })
      .filter(Boolean);
  }

  return [];
};

export const buildAiContext = (overrides = {}) => {
  const { documentId } = getSessionTokens();
  const { currentPage, metadata, epubCurrentChapterNumber } =
    useReaderStore.getState();
  return {
    document_id: documentId,
    chapter:
      overrides.chapter ||
      deriveChapterLabel(metadata, currentPage, epubCurrentChapterNumber),
    page_number:
      overrides.page_number != null ? overrides.page_number : currentPage || 1,
  };
};

const aiRequest = async (fn) => {
  try {
    return unwrapData(await fn());
  } catch (error) {
    throw toAiError(error);
  }
};

export const getAiAvailability = async ({ force = false } = {}) => {
  const { grantId, rstToken, documentId } = await getAuth();

  if (!force) {
    const cached = availabilityCache.get(documentId);
    if (cached) return cached;
    const inflight = availabilityInflight.get(documentId);
    if (inflight) return inflight;
  }

  const request = aiRequest(() =>
    axios({
      method: "get",
      url: `${getAiBaseUrl()}/v1/extension/${grantId}/documents/${documentId}/ai-availability`,
      headers: authHeaders(rstToken),
      timeout: AI_TIMEOUT_MS,
    }),
  )
    .then((data) => {
      if (data?.ai_available) {
        availabilityCache.set(documentId, data);
      } else {
        availabilityCache.delete(documentId);
      }
      availabilityInflight.delete(documentId);
      return data;
    })
    .catch((error) => {
      availabilityInflight.delete(documentId);
      throw error;
    });

  availabilityInflight.set(documentId, request);
  return request;
};

export const peekAiPreferences = () => preferencesCache;

/**
 * Sync read of language for action `target_language`.
 * Prefs may store a code (e.g. "fil"); actions send the English name
 * (e.g. "Filipino") so the LLM recognizes it reliably.
 */
export const getAiTargetLanguage = () =>
  toAiLanguageName(preferencesCache?.ai_language || DEFAULT_AI_LANGUAGE);

/**
 * Client-side flag (ai_v4 prefs). When false, omit selected `content` and
 * chapter/page grounding; `document_id` is still sent (API-required).
 * Defaults true.
 */
export const isContextInclusionEnabled = () =>
  preferencesCache?.context_inclusion_enabled !== false;

export const getAiPreferences = async ({ force = false } = {}) => {
  if (!force) {
    if (preferencesCache) return preferencesCache;
    if (preferencesInflight) return preferencesInflight;
  }

  const { grantId, rstToken } = await getAuth();

  const request = aiRequest(() =>
    axios({
      method: "get",
      url: `${getAiBaseUrl()}/v1/extension/${grantId}/preferences`,
      headers: authHeaders(rstToken),
      timeout: AI_TIMEOUT_MS,
    }),
  )
    .then((data) => {
      preferencesCache = data && typeof data === "object" ? { ...data } : {};
      preferencesInflight = null;
      return preferencesCache;
    })
    .catch((error) => {
      preferencesInflight = null;
      throw error;
    });

  preferencesInflight = request;
  return request;
};

/**
 * Merge `partial` onto the last known prefs (fetch first if cold), then PUT
 * the full object (ai_v4 §2.2). Writes are queued so concurrent callers
 * (language + preference toggles) cannot overwrite each other.
 *
 * Callers may pass any subset, e.g. `{ ai_language }`, `{ citation_enabled }`,
 * or `{ context_inclusion_enabled }`.
 */
export const updateAiPreferences = async (partial = {}) => {
  const run = async () => {
    const current = preferencesCache || (await getAiPreferences());
    const previous = { ...current };
    const next = { ...current, ...partial };

    // Optimistic cache so actions / UI see the new values immediately.
    preferencesCache = next;

    try {
      const { grantId, rstToken } = await getAuth();
      const data = await aiRequest(() =>
        axios({
          method: "put",
          url: `${getAiBaseUrl()}/v1/extension/${grantId}/preferences`,
          data: next,
          headers: jsonAuthHeaders(rstToken),
          timeout: AI_TIMEOUT_MS,
        }),
      );
      preferencesCache =
        data && typeof data === "object" ? { ...data } : { ...next };
      return preferencesCache;
    } catch (error) {
      preferencesCache = previous;
      throw error;
    }
  };

  const queued = preferencesWriteChain.then(run, run);
  // Keep the chain alive after failures so later writes still run.
  preferencesWriteChain = queued.catch(() => {});
  return queued;
};

/**
 * Shared POST /actions body (ai_v5).
 * Streaming is opted in only via `is_streaming: true` — not query/Accept.
 */
const buildAiActionRequest = async ({
  action_type,
  prompt = "",
  content = "",
  action_params = {},
  conversation_id,
  target_language,
  context_overrides,
  is_streaming = false,
} = {}) => {
  const { grantId, rstToken, documentId } = await getAuth();

  // Only `custom` (Ask AI) needs conversation history. One-shot actions
  // (summarize / explain / quiz / translation) must not send conversation_id.
  const isConversational = action_type === "custom";
  if (
    isConversational &&
    conversationDocumentId &&
    conversationDocumentId !== documentId
  ) {
    resetAiConversation();
  }

  const resolvedLanguage = toAiLanguageName(
    target_language || getAiTargetLanguage() || DEFAULT_AI_LANGUAGE_NAME,
  );

  // Prefs are client-enforced: API persists context_inclusion_enabled but
  // does not apply it. When off, skip selected text + chapter/page grounding
  // for context-free chat — document_id is still required by the actions API.
  const includeReadingContext = isContextInclusionEnabled();
  const body = {
    action_type,
    prompt,
    content: includeReadingContext ? content : "",
    context: includeReadingContext
      ? buildAiContext(context_overrides)
      : { document_id: documentId },
    action_params,
    target_language: resolvedLanguage,
  };

  if (isConversational) {
    const convo = conversation_id || conversationId;
    if (convo) body.conversation_id = convo;
  }

  if (is_streaming) body.is_streaming = true;

  return {
    grantId,
    rstToken,
    documentId,
    isConversational,
    url: `${getAiBaseUrl()}/v1/extension/${grantId}/actions`,
    body,
  };
};

const rememberActionConversation = (data, documentId, isConversational) => {
  if (!isConversational) return;
  rememberConversationId(data?.conversation_id);
  conversationDocumentId = documentId;
};

const readErrorBody = async (response) => {
  const text = await response.text().catch(() => "");
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return { message: text };
  }
};

/**
 * Merge one SSE event (ai_v5):
 * - incremental: `{ content, done: false }` — `content` is the next text delta
 * - final: `{ done: true, answer, citations, credits_state, ... }`
 * - error: `{ done: true, error: true, error_code, error_message }`
 */
const mergeStreamEvent = (state, event) => {
  if (!event || typeof event !== "object") return state;

  // ai_v5: error events set `error: true` with error_code / error_message.
  if (event.error === true) {
    throw new AiError({
      code: event.error_code,
      message: event.error_message || "AI stream error",
      body: event,
    });
  }

  if (!event.done && typeof event.content === "string" && event.content) {
    state.answer = `${state.answer || ""}${event.content}`;
  }

  if (event.done && typeof event.answer === "string") {
    state.answer = event.answer;
  }

  Object.keys(event).forEach((key) => {
    if (key === "content" || key === "done" || key === "error") return;
    if (event[key] !== undefined) state[key] = event[key];
  });

  if (event.done) state.done = true;
  return state;
};

/** Parse complete SSE blocks (`data: <json>\\n\\n`). */
const parseSseBlocks = (chunk) => {
  const events = [];
  String(chunk || "")
    .split(/\r?\n\r?\n/)
    .forEach((block) => {
      const trimmed = block.trim();
      if (!trimmed || trimmed.startsWith(":")) return;

      const dataLines = trimmed
        .split(/\r?\n/)
        .filter((line) => line.startsWith("data:"))
        .map((line) => line.slice(5).trimStart());

      if (!dataLines.length) return;

      const payload = dataLines.join("\n").trim();
      if (!payload || payload === "[DONE]") {
        events.push({ done: true });
        return;
      }
      try {
        events.push(JSON.parse(payload));
      } catch {
        events.push({ content: payload, done: false });
      }
    });
  return events;
};

/**
 * Stream an AI action (ai_v5 §2.3.2).
 * Opt-in: body `is_streaming: true` only — no `?stream=` / special Accept.
 *
 * @param {object} options — same fields as executeAiAction, plus:
 * @param {(partial: object) => void} [options.onUpdate]
 * @param {AbortSignal} [options.signal]
 */
export const executeAiActionStream = async ({
  onUpdate,
  signal,
  ...actionArgs
} = {}) => {
  const { documentId, url, body, rstToken, isConversational } =
    await buildAiActionRequest({
      ...actionArgs,
      is_streaming: true,
    });

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), AI_TIMEOUT_MS);
  const onAbort = () => controller.abort();
  if (signal) {
    if (signal.aborted) controller.abort();
    else signal.addEventListener("abort", onAbort, { once: true });
  }

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${rstToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
      signal: controller.signal,
    });

    if (!response.ok) {
      const errBody = await readErrorBody(response);
      const err = errBody?.error || errBody;
      throw new AiError({
        status: response.status,
        code: err?.code,
        message: err?.message || `AI request failed (${response.status})`,
        body: errBody,
      });
    }

    const contentType = String(response.headers.get("content-type") || "");
    const isEventStream = contentType.includes("text/event-stream");

    // Server ignored is_streaming — fall back to JSON envelope.
    if (!isEventStream || !response.body) {
      const json = await response.json();
      const data = json?.data ?? json;
      rememberActionConversation(data, documentId, isConversational);
      if (typeof onUpdate === "function") {
        onUpdate({ ...data, done: true });
      }
      return data;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    const state = { answer: "", done: false };
    let paintedOnce = false;

    const emit = () => {
      if (typeof onUpdate === "function") onUpdate({ ...state });
    };

    /** Yield so the first token can paint before we drain the rest of the chunk. */
    const yieldForPaint = async (isFirstContent = false) => {
      if (isFirstContent && !paintedOnce) {
        paintedOnce = true;
        await new Promise((resolve) => requestAnimationFrame(resolve));
        return;
      }
      // Let the UI painter / React flush between events in the same TCP chunk.
      await Promise.resolve();
    };

    const consumeEvents = async (events) => {
      for (let i = 0; i < events.length; i += 1) {
        const before = state.answer || "";
        mergeStreamEvent(state, events[i]);
        const changed = (state.answer || "") !== before || state.done;
        if (!changed) continue;

        emit();
        // First token: wait a frame so "Thinking…" clears immediately.
        await yieldForPaint(!before && Boolean(state.answer));
      }
    };

    while (!state.done) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const parts = buffer.split(/\r?\n\r?\n/);
      buffer = parts.pop() || "";

      for (let i = 0; i < parts.length; i += 1) {
        await consumeEvents(parseSseBlocks(`${parts[i]}\n\n`));
      }
    }

    if (buffer.trim()) {
      await consumeEvents(parseSseBlocks(`${buffer}\n\n`));
    }

    state.done = true;
    rememberActionConversation(state, documentId, isConversational);
    emit();
    return state;
  } catch (error) {
    if (error instanceof AiError) throw error;
    if (error?.name === "AbortError") {
      throw new AiError({
        code: "ABORTED",
        message: signal?.aborted
          ? "AI request cancelled"
          : "AI request timed out",
      });
    }
    throw toAiError(error);
  } finally {
    clearTimeout(timeoutId);
    if (signal) signal.removeEventListener("abort", onAbort);
  }
};

export const executeAiAction = async ({
  action_type,
  prompt = "",
  content = "",
  action_params = {},
  conversation_id,
  target_language,
  context_overrides,
} = {}) => {
  const { documentId, url, body, rstToken, isConversational } =
    await buildAiActionRequest({
      action_type,
      prompt,
      content,
      action_params,
      conversation_id,
      target_language,
      context_overrides,
      is_streaming: false,
    });

  const data = await aiRequest(() =>
    axios({
      method: "post",
      url,
      data: body,
      headers: jsonAuthHeaders(rstToken),
      timeout: AI_TIMEOUT_MS,
    }),
  );

  rememberActionConversation(data, documentId, isConversational);
  return data;
};

/**
 * Persist one graded quiz attempt. Server computes is_correct / score —
 * never trust client-reported scores. Requires an authenticated (non-anonymous) session.
 */
export const saveQuizResult = async ({
  document_id,
  conversation_id,
  message_id,
  questions,
  answers,
} = {}) => {
  const { grantId, rstToken, documentId } = await getAuth();
  const body = {
    document_id: document_id || documentId,
    questions,
    answers,
  };
  if (conversation_id) body.conversation_id = conversation_id;
  if (message_id) body.message_id = message_id;

  return aiRequest(() =>
    axios({
      method: "post",
      url: `${getAiBaseUrl()}/v1/extension/${grantId}/quiz-results`,
      data: body,
      headers: jsonAuthHeaders(rstToken),
      timeout: AI_TIMEOUT_MS,
    }),
  );
};

/**
 * List saved quiz results for the current user (newest first).
 * Pass document_id to scope to the open book.
 */
export const listQuizResults = async ({
  document_id,
  page = 1,
  page_size = 20,
} = {}) => {
  const { grantId, rstToken, documentId } = await getAuth();
  const params = { page, page_size };
  const docId = document_id || documentId;
  if (docId) params.document_id = docId;

  return aiRequest(() =>
    axios({
      method: "get",
      url: `${getAiBaseUrl()}/v1/extension/${grantId}/quiz-results`,
      params,
      headers: authHeaders(rstToken),
      timeout: AI_TIMEOUT_MS,
    }),
  );
};

/** Delete a saved quiz result owned by the current user. */
export const deleteQuizResult = async (resultId) => {
  if (resultId == null) {
    throw new AiError({ message: "Missing quiz result id" });
  }
  const { grantId, rstToken } = await getAuth();
  return aiRequest(() =>
    axios({
      method: "delete",
      url: `${getAiBaseUrl()}/v1/extension/${grantId}/quiz-results/${resultId}`,
      headers: authHeaders(rstToken),
      timeout: AI_TIMEOUT_MS,
    }),
  );
};

/**
 * Fetch contextual pullouts (hints / banner / modal) for the current reading
 * context. ai_v5 §2.5 — runs in the background; callers should not block UI.
 * @returns {Promise<object[]>}
 */
export const fetchPullouts = async ({
  document_id,
  page_number,
  action = "page_loaded",
  reference_text,
  prompt_text,
} = {}) => {
  const { grantId, rstToken, documentId } = await getAuth();
  const pageNumber = Number(page_number);
  const context = {
    document_id: document_id || documentId,
    page_number: Number.isFinite(pageNumber) && pageNumber > 0 ? pageNumber : 1,
  };
  if (action) context.action = action;
  if (reference_text) context.reference_text = reference_text;
  if (prompt_text) context.prompt_text = prompt_text;

  const data = await aiRequest(() =>
    axios({
      method: "post",
      url: `${getAiBaseUrl()}/v1/extension/${grantId}/pullout`,
      data: { context },
      headers: jsonAuthHeaders(rstToken),
      timeout: AI_TIMEOUT_MS,
    }),
  );

  return Array.isArray(data?.pullouts) ? data.pullouts : [];
};
